import React, { useState, useEffect } from 'react';
import { bookmarkAPI } from '../../services/api';
import '../Jobs/JobsPage.css';

const BookmarkModal = ({ isOpen, onClose, talentId, talentName, onBookmarked }) => {
  const [lists, setLists] = useState([]);
  const [loading, setLoading] = useState(false);
  const [creatingList, setCreatingList] = useState(false);
  const [newListTitle, setNewListTitle] = useState('');
  const [newListDescription, setNewListDescription] = useState('');
  const [selectedListId, setSelectedListId] = useState(null);
  const [notes, setNotes] = useState('');
  const [error, setError] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (isOpen && talentId) {
      fetchLists();
      checkBookmarked();
    }
  }, [isOpen, talentId]);

  const fetchLists = async () => {
    try {
      setLoading(true);
      // Only fetch lists that have talents (from talent_bookmarks table)
      const response = await bookmarkAPI.getLists('talents');
      if (response.success) {
        setLists(response.data || []);
      }
    } catch (err) {
      console.error('Error fetching bookmark lists:', err);
      setError('Failed to load bookmark lists');
    } finally {
      setLoading(false);
    }
  };

  const checkBookmarked = async () => {
    try {
      const response = await bookmarkAPI.checkBookmarked(talentId);
      if (response.success && response.is_bookmarked && response.lists.length > 0) {
        // Pre-select the first list if already bookmarked
        setSelectedListId(response.lists[0].id);
      }
    } catch (err) {
      console.error('Error checking bookmark status:', err);
    }
  };

  const handleCreateList = async () => {
    if (!newListTitle.trim()) {
      setError('Please enter a list title');
      return;
    }

    try {
      setCreatingList(true);
      setError(null);
      const response = await bookmarkAPI.createList({
        title: newListTitle.trim(),
        description: newListDescription.trim() || null,
      });

      if (response.success) {
        setLists(prev => [response.data, ...prev]);
        setSelectedListId(response.data.id);
        setNewListTitle('');
        setNewListDescription('');
        setCreatingList(false);
      }
    } catch (err) {
      console.error('Error creating bookmark list:', err);
      setError(err.response?.data?.message || 'Failed to create bookmark list');
    } finally {
      setCreatingList(false);
    }
  };

  const handleSave = async () => {
    if (!selectedListId) {
      setError('Please select or create a bookmark list');
      return;
    }

    try {
      setSaving(true);
      setError(null);
      const response = await bookmarkAPI.addTalent({
        bookmark_list_id: selectedListId,
        talent_id: talentId,
        notes: notes.trim() || null,
      });

      if (response.success) {
        if (onBookmarked) {
          onBookmarked(selectedListId);
        }
        onClose();
      }
    } catch (err) {
      console.error('Error saving bookmark:', err);
      setError(err.response?.data?.message || 'Failed to save bookmark');
    } finally {
      setSaving(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="jobs-filter-mobile-modal-overlay" onClick={onClose}>
      <div className="jobs-filter-mobile-modal" onClick={(e) => e.stopPropagation()} style={{ 
        maxWidth: '500px', 
        width: '90%',
        maxHeight: '90vh',
        overflowY: 'auto',
        margin: '20px auto'
      }}>
        <div className="jobs-filter-mobile-modal-header">
          <h3>Save to Bookmark List</h3>
          <button 
            className="jobs-filter-mobile-close-btn"
            onClick={onClose}
          >
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <line x1="18" y1="6" x2="6" y2="18"></line>
              <line x1="6" y1="6" x2="18" y2="18"></line>
            </svg>
          </button>
        </div>

        <div className="jobs-filter-mobile-modal-content" style={{ 
          padding: 'clamp(16px, 4vw, 20px)',
          maxHeight: 'calc(90vh - 60px)',
          overflowY: 'auto'
        }}>
          {error && (
            <div style={{ 
              padding: '12px', 
              background: '#fef2f2', 
              border: '1px solid #fecaca', 
              borderRadius: '6px', 
              marginBottom: '16px',
              color: '#991b1b'
            }}>
              {error}
            </div>
          )}

          <div style={{ marginBottom: '20px' }}>
            <p style={{ marginBottom: '12px', color: '#374151', fontSize: '14px' }}>
              Save <strong>{talentName}</strong> to a bookmark list
            </p>
          </div>

          {/* Create New List Section */}
          <div style={{ marginBottom: '24px', padding: '16px', background: '#f9fafb', borderRadius: '8px' }}>
            <h4 style={{ marginBottom: '12px', fontSize: '16px', fontWeight: 600, color: '#1f2937' }}>
              Create New List
            </h4>
            <div style={{ marginBottom: '12px' }}>
              <input
                type="text"
                placeholder="List title (required)"
                value={newListTitle}
                onChange={(e) => setNewListTitle(e.target.value)}
                style={{
                  width: '100%',
                  padding: '10px 12px',
                  border: '1px solid #d1d5db',
                  borderRadius: '6px',
                  fontSize: 'clamp(14px, 3.5vw, 16px)',
                  marginBottom: '8px',
                  boxSizing: 'border-box'
                }}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && newListTitle.trim()) {
                    handleCreateList();
                  }
                }}
              />
              <textarea
                placeholder="Description (optional)"
                value={newListDescription}
                onChange={(e) => setNewListDescription(e.target.value)}
                rows={2}
                style={{
                  width: '100%',
                  padding: '10px 12px',
                  border: '1px solid #d1d5db',
                  borderRadius: '6px',
                  fontSize: 'clamp(14px, 3.5vw, 16px)',
                  resize: 'vertical',
                  fontFamily: 'inherit',
                  boxSizing: 'border-box'
                }}
              />
            </div>
            <button
              onClick={handleCreateList}
              disabled={creatingList || !newListTitle.trim()}
              style={{
                padding: '8px 16px',
                background: creatingList || !newListTitle.trim() ? '#d1d5db' : '#7a5fb8',
                color: 'white',
                border: 'none',
                borderRadius: '6px',
                fontSize: '14px',
                fontWeight: 600,
                cursor: creatingList || !newListTitle.trim() ? 'not-allowed' : 'pointer',
                transition: 'all 0.2s ease'
              }}
            >
              {creatingList ? 'Creating...' : 'Create List'}
            </button>
          </div>

          {/* Existing Lists Section */}
          <div style={{ marginBottom: '20px' }}>
            <h4 style={{ marginBottom: '12px', fontSize: '16px', fontWeight: 600, color: '#1f2937' }}>
              Select Existing List
            </h4>
            {loading ? (
              <div style={{ textAlign: 'center', padding: '20px' }}>
                <div className="jobs-spinner" style={{ margin: '0 auto' }}></div>
              </div>
            ) : lists.length === 0 ? (
              <p style={{ color: '#6b7280', fontSize: '14px', textAlign: 'center', padding: '20px' }}>
                No bookmark lists yet. Create one above.
              </p>
            ) : (
              <div style={{ 
                maxHeight: 'min(200px, 40vh)', 
                overflowY: 'auto',
                WebkitOverflowScrolling: 'touch'
              }}>
                {lists.map((list) => (
                  <div
                    key={list.id}
                    onClick={() => setSelectedListId(list.id)}
                    style={{
                      padding: '12px',
                      marginBottom: '8px',
                      border: selectedListId === list.id ? '2px solid #7a5fb8' : '1px solid #e5e7eb',
                      borderRadius: '6px',
                      cursor: 'pointer',
                      background: selectedListId === list.id ? '#f5f3ff' : 'white',
                      transition: 'all 0.2s ease'
                    }}
                  >
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                      <div style={{ flex: 1 }}>
                        <div style={{ fontWeight: 600, color: '#1f2937', marginBottom: '4px' }}>
                          {list.title}
                        </div>
                        {list.description && (
                          <div style={{ fontSize: '12px', color: '#6b7280', marginBottom: '4px' }}>
                            {list.description}
                          </div>
                        )}
                        <div style={{ fontSize: '12px', color: '#9ca3af' }}>
                          {list.talents_count || 0} {list.talents_count === 1 ? 'talent' : 'talents'}
                        </div>
                      </div>
                      {selectedListId === list.id && (
                        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#7a5fb8" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                          <path d="M20 6L9 17l-5-5"></path>
                        </svg>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Notes Section */}
          <div style={{ marginBottom: '20px' }}>
            <label style={{ display: 'block', marginBottom: '8px', fontSize: '14px', fontWeight: 500, color: '#374151' }}>
              Notes (optional)
            </label>
            <textarea
              placeholder="Add any notes about this talent..."
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              rows={3}
              style={{
                width: '100%',
                padding: '10px 12px',
                border: '1px solid #d1d5db',
                borderRadius: '6px',
                fontSize: 'clamp(14px, 3.5vw, 16px)',
                resize: 'vertical',
                fontFamily: 'inherit',
                boxSizing: 'border-box'
              }}
            />
          </div>

          {/* Action Buttons */}
          <div style={{ 
            display: 'flex', 
            gap: '12px', 
            justifyContent: 'flex-end',
            flexWrap: 'wrap'
          }}>
            <button
              onClick={onClose}
              style={{
                padding: '10px 20px',
                background: 'transparent',
                color: '#6b7280',
                border: '1px solid #d1d5db',
                borderRadius: '6px',
                fontSize: '14px',
                fontWeight: 600,
                cursor: 'pointer',
                transition: 'all 0.2s ease'
              }}
            >
              Cancel
            </button>
            <button
              onClick={handleSave}
              disabled={!selectedListId || saving}
              style={{
                padding: '10px 20px',
                background: !selectedListId || saving ? '#d1d5db' : '#7a5fb8',
                color: 'white',
                border: 'none',
                borderRadius: '6px',
                fontSize: '14px',
                fontWeight: 600,
                cursor: !selectedListId || saving ? 'not-allowed' : 'pointer',
                transition: 'all 0.2s ease'
              }}
            >
              {saving ? 'Saving...' : 'Save'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default BookmarkModal;

